import inflection from 'inflection';
import _ from 'lodash';
import React, { useMemo, useState } from 'react';
import useAppSelector, { useAppDispatch } from '../../app/redux/useAppSelector';
import Button from '../../components/Button';
import ConfirmationModal from '../../components/ConfirmationModal';
import EmptyState from '../../components/EmptyState';
import { MenuItem } from '../../components/Menu';
import RightPanel from '../../components/RightPanel';
import Toast from '../../components/Toast';
import InvitationModal from '../../invitation/InvitationModal';
import { inputToOptions } from '../../invitation/InvitationUserSelect';
import useInvitationEmailOption from '../../invitation/useInvitationEmailOption';
import type { EntitySelectConfig } from '../../search/entitySearch/CategorizedEntitySelect';
import CategorizedEntitySelect from '../../search/entitySearch/CategorizedEntitySelect';
import { UIActions } from '../../ui/uiSlice';
import { useAllUsersMap } from '../../user/useAllUsers';
import useSlug from '../../utils/router/useSlug';
import getFirstName from '../../utils/text/getFirstName';
import TopicIcon from '../TopicIcon';
import topicPolicyCheck, {
  isOwner,
  TopicPolicyAction,
} from '../topicPolicyCheck';
import { useAllTopicMap } from '../useAllTopics';
import {
  useAddTopicGroup,
  useAddTopicUser,
  useRemoveTopicGroup,
  useRemoveTopicUser,
  useUpdateInheritParent,
  useUpdateTopicGroup,
  useUpdateTopicUser,
} from '../useTopicUserMutations';
import getTopicUsers, {
  getInheritedUsers,
  getTopicUserIdSet,
} from '../utils/getTopicUsers';
import TopicGroupRow from './TopicGroupRow';
import TopicUserCollapsibleList from './TopicUserCollapsibleList';
import TopicUserList from './TopicUserList';
import styles from './TopicUsersPanel.module.scss';
import getTopicAccessWarnings from './getTopicAccessWarning';

const joinNames = (obj: { name: string }[]) => {
  const names = obj.map(({ name }) => name);
  const { length } = names;
  return length <= 1
    ? names.join('')
    : // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      `${names.slice(0, -1).join(', ')}, and ${names[length - 1]}`;
};

const removeDirectOwner = <T extends { owners?: { id: string }[] }>(
  topic: T,
  { id }: { id: string },
) =>
  topic && {
    ...topic,
    owners: topic.owners?.filter((owner) => owner.id !== id),
  };

const TOPIC_JOIN_TIP =
  'Become a member to receive notifications for new posts and edits in this topic.';

interface TopicUsersPanelProps {
  close: () => unknown;
}

const TopicUsersPanel = ({ close }: TopicUsersPanelProps) => {
  const [topicId] = useSlug('topicSlug');
  const currentUser = useAppSelector((state) => state.session.user);
  const dispatch = useAppDispatch();
  const allUsersMap = useAllUsersMap();
  const allTopicsMap = useAllTopicMap();
  const [input, setInput] = useState('');
  const hasInput = !!input;

  const baseTopic = allTopicsMap[topicId];
  const parentId = baseTopic?.parent?.id;
  const fullParent = parentId ? allTopicsMap[parentId] : undefined;

  const topic = useMemo(
    () => baseTopic && { ...baseTopic, parent: fullParent },
    [baseTopic, fullParent],
  );

  const addTopicUser = useAddTopicUser();
  const removeTopicUser = useRemoveTopicUser();
  const updateTopicUser = useUpdateTopicUser();
  const addTopicGroup = useAddTopicGroup();
  const removeTopicGroup = useRemoveTopicGroup();
  const updateTopicGroup = useUpdateTopicGroup();
  const updateInheritParent = useUpdateInheritParent();

  const getInvitationEmailOption = useInvitationEmailOption();

  const config = useMemo<EntitySelectConfig>(() => {
    if (!topic) {
      return {};
    }

    const topicUserIdSet = getTopicUserIdSet(topic);

    const {
      membership: {
        ownerUserIds,
        memberUserIds,
        ownerGroupIds,
        memberGroupIds,
      },
      parent,
    } = topic;

    const userIdSet = hasInput
      ? // If input is not empty, omit only users which has direct access
        new Set([...(memberUserIds || []), ...(ownerUserIds || [])])
      : // If input is empty, omit users which have access through parent topic or groups
        new Set([...topicUserIdSet.members, ...topicUserIdSet.owners]);

    const inheritedGroupIdsSet = new Set(
      topic.inheritedAncestors.flatMap((ancestor) => [
        ...ancestor.ownerGroups.map((g) => g.id),
        ...ancestor.memberGroups.map((g) => g.id),
      ]),
    );

    const groupIdSet = new Set([
      // If input is empty, omit groups which have access through parent topic
      ...(hasInput ? [] : [...inheritedGroupIdsSet]),
      ...(memberGroupIds || []),
      ...(ownerGroupIds || []),
    ]);

    const isInheritable =
      !!parent &&
      !topic.inheritParent &&
      topicPolicyCheck(TopicPolicyAction.AddRemoveOwner, topic, currentUser);

    return {
      user: { preferredMaxCount: 5, filter: (user) => !userIdSet.has(user.id) },
      group: {
        preferredMaxCount: isInheritable ? 2 : 3,
        filter: (group) => !groupIdSet.has(group.id),
      },
      topic: {
        preferredMaxCount: isInheritable ? 1 : 0,
        label: 'Parent topic',
        filter: (t) => isInheritable && t.id === parent.id,
      },
      new: {
        showIfNoOthersMatch: true,
        getGroup: (inputValue) =>
          getInvitationEmailOption({
            inputValue,
            onSelect: (suggestedEmail) => {
              dispatch(
                UIActions.openModal(InvitationModal, {
                  disableOutsideClose: true,
                  initialOptions: suggestedEmail
                    ? inputToOptions(suggestedEmail) ?? undefined
                    : undefined,
                  initialValues: { topicIds: [topic.id] },
                }),
              );
            },
          }),
      },
      order: ['user', 'group', 'topic', 'new'],
    };
  }, [topic, hasInput, currentUser, getInvitationEmailOption, dispatch]);

  if (!topic) {
    return null;
  }

  const showToast = (
    props: { title: string; message: string },
    duration?: number,
  ) => {
    dispatch(
      UIActions.pushToast(
        ({ onDismiss }) => (
          <Toast
            secondaryText="Dismiss"
            onSecondaryClick={onDismiss}
            onDismiss={onDismiss}
            {...props}
          />
        ),
        duration,
      ),
    );
  };

  const {
    memberGroups,
    members,
    name,
    ownerGroups,
    owners,
    parent,
    requestors,
  } = topic;
  const inheritedParent = topic.inheritParent ? parent : undefined;

  const flattenUsers = getTopicUsers(topic);
  const totalUserCount =
    flattenUsers.owners.length + flattenUsers.members.length;
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
  const currentUserJoined =
    _.some(flattenUsers.owners, { id: currentUser.id }) ||
    _.some(flattenUsers.members, { id: currentUser.id });

  const { owners: inheritedOwners, members: inheritedMembers } =
    getInheritedUsers(topic);

  const canEditOwners =
    currentUserJoined &&
    topicPolicyCheck(TopicPolicyAction.AddRemoveOwner, topic, currentUser);

  const canEditMembers =
    currentUserJoined &&
    topicPolicyCheck(TopicPolicyAction.AddRemoveMember, topic, currentUser);

  // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
  const currentUserCanJoin =
    !currentUserJoined &&
    topicPolicyCheck(TopicPolicyAction.AddRemoveMember, topic, currentUser);

  const handleRemoveGroup = async (group: { id: string; name: string }) => {
    await removeTopicGroup(topic, group);
    showToast({
      title: `${group.name} has been removed as a member`,
      message: `Some ${group.name} members may still be members of ${topic.name} directly or through a group.`,
    });
  };

  return (
    <RightPanel
      header={
        totalUserCount > 0
          ? `${totalUserCount} ${inflection.inflect('member', totalUserCount)}`
          : ''
      }
      close={close}
      footer={
        totalUserCount > 0 && (canEditMembers || currentUserCanJoin) ? (
          <>
            {canEditMembers && (
              <div className={styles.footer}>
                <h4>Add other members</h4>
                <div className={styles.helpText}>
                  Members will receive notifications for new posts, and can
                  manage other members in this topic.
                </div>
                <CategorizedEntitySelect
                  placeholder={`Add member to ${topic.name}`}
                  aria-label={`Add member to ${topic.name}`}
                  maxOptionsToShow={8}
                  config={config}
                  value={null}
                  onChange={(value) => {
                    if (!value) {
                      return;
                    }
                    if (value.type === 'user') {
                      addTopicUser(topic, value.user);
                    } else if (value.type === 'group') {
                      addTopicGroup(topic, value.group);
                    } else if (value.type === 'topic') {
                      updateInheritParent(topic, true);
                    }
                  }}
                  inputValue={input}
                  onInputChange={setInput}
                />
              </div>
            )}
            {currentUserCanJoin && (
              <div className={styles.footer}>
                <h4>Join topic</h4>
                <div className={styles.helpText}>{TOPIC_JOIN_TIP}</div>
                <Button
                  block
                  variant="primary"
                  onClick={() => addTopicUser(topic, currentUser)}
                >
                  Join {name}
                </Button>
              </div>
            )}
          </>
        ) : undefined
      }
    >
      <div className={styles.container}>
        {totalUserCount === 0 && (
          <EmptyState
            imageName="genericAlt"
            title="No members yet"
            message={TOPIC_JOIN_TIP}
            action={
              <Button
                onClick={() => addTopicUser(topic, currentUser)}
                variant="primary"
              >
                Join {name}
              </Button>
            }
            vertical
          />
        )}
        {totalUserCount > 0 && (
          <div className={styles.list}>
            {inheritedParent && (
              <TopicUserCollapsibleList
                icon={<TopicIcon />}
                members={inheritedMembers}
                name={inheritedParent.name}
                owners={inheritedOwners}
                menu={
                  canEditOwners && (
                    <MenuItem
                      danger
                      onClick={async () => {
                        await updateInheritParent(topic, false);
                        dispatch(
                          UIActions.pushToast(
                            // eslint-disable-next-line react/no-unstable-nested-components -- Fix next time editing the file
                            ({ onDismiss }) => (
                              <Toast
                                title={`${inheritedParent.name} members are no longer inherited`}
                                message={`Some ${inheritedParent.name} members may still be members of ${topic.name} directly or through a group.`}
                                secondaryText="Undo"
                                onSecondaryClick={() => {
                                  updateInheritParent(topic, true);
                                  onDismiss();
                                }}
                                onDismiss={onDismiss}
                              />
                            ),
                            6000,
                          ),
                        );
                      }}
                    >
                      Remove from topic
                    </MenuItem>
                  )
                }
              />
            )}
            {ownerGroups.map((group) => (
              <TopicGroupRow
                key={group.id}
                group={group}
                owner
                {...(canEditOwners
                  ? {
                      onToggleOwnership: () =>
                        updateTopicGroup(topic, group, 'owner', 'member'),
                      onRemove: () => handleRemoveGroup(group),
                    }
                  : {})}
              />
            ))}
            {memberGroups.map((group) => (
              <TopicGroupRow
                key={group.id}
                group={group}
                onToggleOwnership={
                  canEditOwners
                    ? () => updateTopicGroup(topic, group, 'member', 'owner')
                    : undefined
                }
                onRemove={
                  canEditMembers ? () => handleRemoveGroup(group) : undefined
                }
              />
            ))}
            <TopicUserList
              topic={topic}
              members={members}
              owners={owners}
              requestors={requestors}
              removeUser={async (user) => {
                const userPreviousRole = isOwner(topic, user)
                  ? 'owner'
                  : 'member';

                await removeTopicUser(topic, user);
                const fullUser = allUsersMap[user.id];

                if (!fullUser) {
                  return;
                }

                const firstName = getFirstName(fullUser.name) ?? '';
                const access = getTopicAccessWarnings(topic, fullUser);

                let message = `${firstName} is no longer a member of ${topic.name}.`;

                if (access?.groups?.length) {
                  message = `${firstName} is no longer a direct member of ${
                    topic.name
                  } but is still an ${
                    access.level
                  } through the group ${joinNames(access.groups)}.`;
                }

                if (access?.parent) {
                  message = `${firstName} is no longer a direct member of ${topic.name}
                    but is still an ${access.level} through the parent topic ${access.parent.name}.`;
                }

                showToast(
                  {
                    title: `${firstName} has been removed as a direct ${userPreviousRole}`,
                    message,
                  },
                  7000,
                );
              }}
              updateUser={(user, oldRole, newRole) => {
                const doUpdate = async () => {
                  await updateTopicUser(topic, user, oldRole, newRole);
                  const fullUser = allUsersMap[user.id];

                  if (!fullUser) {
                    return;
                  }
                  if (oldRole === 'owner' && newRole === 'member') {
                    const firstName = getFirstName(fullUser.name) ?? '';
                    const { level: role, groups } =
                      getTopicAccessWarnings(topic, fullUser) || {};

                    if (role === 'owner') {
                      let message = '';
                      if (groups) {
                        message = `${firstName} is still an owner through groups ${joinNames(
                          groups,
                        )}`;
                      } else if (inheritedParent) {
                        message = `${firstName} is still an owner through the parent topic ${inheritedParent.name}`;
                      }

                      showToast(
                        {
                          title: `${firstName} still has owner privileges`,
                          message,
                        },
                        4000,
                      );
                    }
                  }
                };

                const shouldShowSelfDemotionConfirmation =
                  user.id === currentUser.id &&
                  currentUser.type !== 'ADMIN' &&
                  oldRole === 'owner' &&
                  newRole === 'member' &&
                  !isOwner(removeDirectOwner(topic, user), user);

                if (shouldShowSelfDemotionConfirmation) {
                  dispatch(
                    UIActions.openModal(ConfirmationModal, {
                      title: 'Demote yourself?',
                      message:
                        'You will not be able to promote yourself back to an owner.',
                      confirmButtonText: 'Demote',
                      onConfirm: doUpdate,
                    }),
                  );
                } else {
                  doUpdate();
                }
              }}
            />
            {parent == null && currentUser.type === 'GUEST' && (
              <div className={styles.guestWarning}>
                As a guest, you may not be seeing all members of this topic.
              </div>
            )}
          </div>
        )}
      </div>
    </RightPanel>
  );
};

export default TopicUsersPanel;
